import React, { useCallback, useEffect, useState } from 'react';

export const useRegistration = () => {
    const [state, setState] = useState({
        email: '',
        password: '',
        acceptEULA: false,
        name: '',
        phoneNumber: '',
        phoneConfirmed: false,
        code: '',
        city: '',
        country: '',
        activity: '',
        needShowLoader: false,
        redirectUrl: null,
        r: null,
    });

    useEffect(() => {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        const r = params.get('r');
        if (r) {
            setState({ ...state, r: Number(r) });
        }
    }, []);

    const [serverErrors, setServerErrors] = useState({
        code: null,
        email: null,
    });
    const [token, setToken] = useState('');
    const [step, setStep] = useState(0);

    const updateEmail = useCallback((email) => {
        setState((prevState) => ({ ...prevState, email }));
    }, []);

    const updateName = useCallback((name) => {
        setState((prevState) => ({ ...prevState, name }));
    }, []);

    const updatePassword = useCallback((password) => {
        setState((prevState) => ({ ...prevState, password }));
    }, []);

    const updateAcceptEULA = useCallback((value) => {
        setState((prevState) => ({ ...prevState, acceptEULA: value }));
    }, []);

    const updatePhone = useCallback((value) => {
        setState((prevState) => ({ ...prevState, phoneNumber: value }));
    }, []);

    const updatePhoneConfirmed = useCallback((value) => {
        setState((prevState) => ({ ...prevState, phoneConfirmed: value }));
    }, []);

    const updateCode = useCallback((value) => {
        setState((prevState) => ({ ...prevState, code: value }));
    }, []);

    const updateActivity = useCallback((value) => {
        setState((prevState) => ({ ...prevState, activity: value }));
    }, []);

    const updateCity = useCallback((value) => {
        setState((prevState) => ({ ...prevState, city: value }));
    }, []);
    const updateCountry = useCallback((value) => {
        setState((prevState) => ({ ...prevState, country: value }));
    }, []);

    const stepBack = useCallback(() => {
        if (step === 0) {
            return;
        }
        if (step === 2) {
            setToken('');
        }
        setStep(step - 1);
    }, [step, setStep]);

    const removeButtonLoader = useCallback(() => {
        setState((prevState) => ({ ...prevState, needShowLoader: false }));
    }, []);

    const addButtonLoader = useCallback(() => {
        setState((prevState) => ({ ...prevState, needShowLoader: true }));
    }, []);

    const updateRedirectUrl = useCallback((value) => {
        setState((prevState) => ({ ...prevState, redirectUrl: value }));
    }, []);

    const sendValidateFirstStepRequest = useCallback(() => {
        addButtonLoader();
        const { email, name, password, acceptEULA } = state;
        const url = helpable.replaceUrlParameters(routable.getRoute('validateFirstStep'));

        if (!acceptEULA) {
            removeButtonLoader();
            return;
        }

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ email, name, plainPassword: password }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.errors) {
                    setServerErrors((prevState) => ({ ...prevState, email: data.errors.email }));
                }
                if (data.response.success) {
                    setStep(step + 1);
                }
                removeButtonLoader();
            })
            .catch(() => {
                removeButtonLoader();
            });
    }, [addButtonLoader, removeButtonLoader, state, step]);

    const sendPhoneForSendOneTimeCode = useCallback(() => {
        addButtonLoader();
        const url = helpable.replaceUrlParameters(routable.getRoute('sendOneTimeCode'));

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
            },
            body: JSON.stringify({ phone: state.phoneNumber, token }),
        })
            .then((response) => response.json())
            .then((data) => {
                removeButtonLoader();
                if (data.response.success && step === 1) {
                    setStep(step + 1);
                }
            })
            .catch(() => {
                removeButtonLoader();
            });
    }, [addButtonLoader, removeButtonLoader, state.phoneNumber, step, token]);

    const sendConfirmationCode = useCallback(() => {
        addButtonLoader();
        const url = helpable.replaceUrlParameters(routable.getRoute('validateConfirmationCode'));

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
            },
            body: JSON.stringify({ phone: state.phoneNumber, code: state.code }),
        })
            .then((response) => response.json())
            .then((data) => {
                removeButtonLoader();
                if (data.response.success) {
                    setState((prevState) => ({ ...prevState, phoneConfirmed: true }));
                    setStep(step + 1);
                } else {
                    setServerErrors((prevState) => ({
                        ...prevState,
                        code: i18next.t('sendConfirmationCode.fetch.then.invalid_code'),
                    }));
                }
            })
            .catch(() => {
                removeButtonLoader();
            });
    }, [addButtonLoader, removeButtonLoader, state.code, state.phoneNumber, step]);

    const sendValidateFourthStepRequest = useCallback(() => {
        addButtonLoader();
        const { email, name, password, phoneNumber, activity, city, country, r } = state;
        const url = helpable.replaceUrlParameters(routable.getRoute('userRegistration'));
        const bodyJSON = {
            email,
            name,
            phone: phoneNumber,
            plainPassword: password,
            fieldActivity: activity,
            city,
            country,
            _token: window.csrf_token,
        };

        if (r) {
            bodyJSON.r = r;
        }

        // console.log('bodyJson', bodyJSON);

        fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=utf-8',
            },
            body: JSON.stringify(bodyJSON),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.success) {
                    setStep(step + 1);
                    updateRedirectUrl(data.redirectUrl);
                }
                removeButtonLoader();
            })
            .catch(() => {
                removeButtonLoader();
            });
    }, [addButtonLoader, removeButtonLoader, state, step, updateRedirectUrl]);

    return {
        state,
        updateEmail,
        updateName,
        updatePassword,
        updateAcceptEULA,
        updateActivity,
        updateCity,
        updateCountry,
        updatePhone,
        updateCode,
        updatePhoneConfirmed,
        step,
        stepBack,
        sendValidateFirstStepRequest,
        sendValidateFourthStepRequest,
        sendPhoneForSendOneTimeCode,
        sendConfirmationCode,
        serverErrors,
        setServerErrors,
        token,
        setToken,
        setStep,
    };
};
